import React, { useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { RootState, useAppDispatch, useAppSelector } from '../../../store'
import api from '../../../api'
import {
  HealthState,
  setForWho,
  setInsurerInfo,
  setInsurerObj,
  setInsurerTranslationInfo,
} from '../../../store/healthSlice'
import { AuthState } from '../../../store/authSlice'
import { useSelector } from 'react-redux'
import ScreenHeader from '../../../components/v2/ScreenHeader'
import Title from '../../../components/v2/Title'
import Dropdown from '../../../components/v2/Dropdown'
import Input from '../../../components/v2/Input'
import * as Yup from 'yup'
import { useFormik } from 'formik'
import Loader from '../../../components/Loader'
import WarningMessage from '../../../components/v2/WarningMessage'
import GenderCard from '../../../components/v2/GenderCard'
import Col from 'react-bootstrap/esm/Col'
import UploadFile from '../../../components/v2/UploadFile'
import NavigationButton from '../../../components/v2/buttons/NavigationButton'
import styled from 'styled-components'
import BirthDayInput from '../../../components/v2/BirthDayInput'
import Colors from '../../../assets/styles/Colors'
import Responsive from '../../../assets/config/Responsive'
import HealthProgressbar from '../../../components/v2/progrees-bars/HealthProgressbar'
import BackgroundIcon from '../../../components/v2/BackgroundIcon'
import { AppState } from '../../../store/appSlice'

const HealthInsurer = () => {
  //
  const navigate = useNavigate()
  //
  const { t } = useTranslation()
  //
  const dispatch = useAppDispatch()
  //
  const { lang } = useAppSelector((root: { app: AppState }) => root.app)
  //
  const auth = useSelector<RootState, AuthState>((state) => state.auth)
  //
  const { checkout, screenHeader } = useAppSelector((data: { health: HealthState }) => data.health)
  //
  // countries array from api
  const { data: countries, isLoading: isCountriesLoading } = api.useGetCountriesQuery(undefined)
  //
  //
  const fakeData = {
    translations: {
      ge: {
        stepName: 'თქვენი პირადი ინფორმაცია',
      },
      en: {
        stepName: 'Your Personal Info',
      },
    },
  }
  //
  const stepName = fakeData.translations[lang].stepName
  //
  //
  // english letter regex
  const englishLetterRegex = /^[A-Za-z]+$/
  // georgian letter regex
  const georgianLetterRegex = /^[\u10D0-\u10FF]*$/
  //
  // yup validations schema
  const PersonalInfoValidationSchema = Yup.object({
    firstName: Yup.string()
      .min(2, `${t('minNameLetterLenght')}`)
      .matches(/^\S*$/, `${t('spaceValidation')}`)
      .matches(englishLetterRegex, `${t('onlyEnglishLetters')}`)
      .required(`${t('required')}`),
    lastName: Yup.string()
      .min(2, `${t('minSurnameLetterLenght')}`)
      .matches(/^\S*$/, `${t('spaceValidation')}`)
      .matches(englishLetterRegex, `${t('onlyEnglishLetters')}`)
      .required(`${t('required')}`),
    firstNameGe: Yup.string()
      .min(2, `${t('minNameLetterLenght')}`)
      .matches(/^\S*$/, `${t('spaceValidation')}`)
      .matches(georgianLetterRegex, `${t('onlyGeorgianLetters')}`)
      .required(`${t('required')}`),
    lastNameGe: Yup.string()
      .min(2, `${t('minSurnameLetterLenght')}`)
      .matches(/^\S*$/, `${t('spaceValidation')}`)
      .matches(georgianLetterRegex, `${t('onlyGeorgianLetters')}`)
      .required(`${t('required')}`),
    idNumber: Yup.string()
      .min(11, `${t('personalNumberValidation')}`)
      .max(11, `${t('personalNumberValidation')}`)
      .matches(/^\S*$/, `${t('spaceValidation')}`)
      .required(`${t('required')}`),
    gender: Yup.string()
      .matches(/^\S*$/, `${t('spaceValidation')}`)
      .required(`${t('required')}`),
  })
  //
  const MainValidationSchema = PersonalInfoValidationSchema
  //
  // initial values
  const initialValues = {
    firstName: checkout.insurer.translations?.en?.firstName || '',
    lastName: checkout.insurer.translations?.en?.lastName || '',
    firstNameGe: checkout.insurer.translations?.ge?.firstName || '',
    lastNameGe: checkout.insurer.translations?.ge?.lastName || '',
    idNumber: checkout.insurer.idNumber || '',
    gender: checkout.insurer.gender || '',
    // passportNumber: checkout.insurer.passportNumber || '',
  }
  // formi state
  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: MainValidationSchema,
    enableReinitialize: true,
    onSubmit: (values) => {
      console.log(values)
    },
  })
  //
  //
  const calculateAgeFromDate = (dateString: any) => {
    if (typeof dateString === 'string') {
      // Remove any leading or trailing white spaces from the string
      dateString = dateString.replace(/\s/g, '')

      // Check if the trimmed date string is not empty
      if (dateString) {
        // Extract day, month, and year from the string
        var parts = dateString.split('/')
        if (parts.length === 3) {
          var day = parseInt(parts[0], 10)
          var month = parseInt(parts[1], 10)
          var year = parseInt(parts[2], 10)

          // Check if the extracted values are valid numbers
          if (!isNaN(day) && !isNaN(month) && !isNaN(year)) {
            var birthDate = new Date(year, month - 1, day)

            // Check if the birthDate is a valid date
            if (
              birthDate instanceof Date &&
              !isNaN(birthDate.getTime()) &&
              birthDate.getFullYear() === year &&
              birthDate.getMonth() === month - 1 &&
              birthDate.getDate() === day
            ) {
              // Calculate the difference in years
              var ageDiff = Date.now() - birthDate.getTime()
              var ageDate = new Date(ageDiff)

              return Math.abs(ageDate.getUTCFullYear() - 1970)
            }
          }
        }
      }
    }

    return null
  }

  //

  const diffage = calculateAgeFromDate(checkout.insurer?.dateOfbirth)
  //
  // splittin image url for showing in the box as title
  const splittingImageTitle = (image: any) => {
    const url: string | URL = image

    const pathName = new URL(url).pathname

    const pathSegments = pathName.split('/')

    const fileName = pathSegments[pathSegments.length - 1]

    const titleSegment = fileName.split('-')

    const title = titleSegment.slice(1).join('-')

    return title
  }
  //
  //
  //
  const formatDate = (dateString: any) => {
    const parts = dateString.split('-')!
    const formattedDate = `${parts[2]} / ${parts[1]} / ${parts[0]}`
    return formattedDate
  }
  //
  const formattedDateString = formatDate(auth.user?.birthday || '')
  //
  const setUserCallBack = useCallback(() => {
    if (auth && auth.user) {
      return [
        dispatch(
          setInsurerObj({
            cityzenship: auth.user.citizenship,
            dateOfbirth: formattedDateString as unknown as string,
            idNumber: auth.user.personalNumber,
            gender: auth.user.gender,
            consentTerm: auth.user.agreeToTerms.consentTerm,
            privacyTerm: auth.user.agreeToTerms.privacyTerm,
            translations: {
              ge: {
                firstName: auth?.user?.firstNameGe,
                lastName: auth?.user?.lastNameGe,
              },
              en: {
                firstName: auth?.user?.firstName,
                lastName: auth?.user?.lastName,
              },
            },
          }),
        ),
      ]
    }
  }, [auth, formattedDateString, dispatch])
  //
  //
  useEffect(() => {
    setUserCallBack()
    return () => {}
  }, [setUserCallBack])
  //
  //
  // declaring insurance type if someone visits via url
  useEffect(() => {
    dispatch(setForWho('for-other'))
  }, [dispatch])

  //
  const isHealthPersonalInfoLoading = false
  //
  const MainLoading = isCountriesLoading || isHealthPersonalInfoLoading
  //

  //
  return MainLoading ? (
    <Loader />
  ) : (
    <Wrapper>
      <ScreenHeader totalCount={screenHeader.totalCount} passedCount={screenHeader.passedCount} />
      <Content>
        <Title text={t('yourPersonalInfo')} />

        <HealthProgressbar stepName={stepName} />

        <WhiteBox>
          <BoxTitle>{t('yourPersonalInfo')}</BoxTitle>

          <Inputs>
            {countries && (
              <Dropdown
                title={`${t('cityzenship')}`}
                isSearchable={true}
                value={countries.find((x) => x.isoCode === checkout.insurer?.cityzenship)?.isoCode}
                options={[
                  ...countries.map((c) => ({
                    value: c.isoCode,
                    label: c.translations?.[lang]?.name,
                  })),
                ]}
                isDisabled={
                  (auth?.user?.citizenship && auth.user?.userType === 'User') ||
                  (checkout.insurer.sgsVerification === true && checkout.insurer.cityzenship)
                    ? true
                    : false
                }
                onChange={(value) =>
                  dispatch(
                    setInsurerInfo({
                      fieldName: 'cityzenship',
                      fieldValue: value.value,
                    }),
                  )
                }
              />
            )}

            <Input
              id="id-number"
              name="idNumber"
              label={`${t('personalNumber')}`}
              type="text"
              placeholder=""
              disabled={
                (auth?.user?.personalNumber && auth.user?.userType === 'User') || checkout.insurer.idNumber
                  ? true
                  : false
              }
              value={checkout.insurer.idNumber || ''}
              onChange={(value) => [
                dispatch(
                  setInsurerInfo({
                    fieldName: 'idNumber',
                    fieldValue: value,
                  }),
                ),
                formik.setFieldValue('idNumber', value),
              ]}
              width={'fullWidth'}
              error={formik.errors.idNumber && checkout.insurer.idNumber ? true : false}
              errorText={formik.errors.idNumber}
              onBlur={formik.handleBlur}
            />

            <BirthDayInputStyle
              width={'fullWidth'}
              id="date-of-birth"
              label={t('dateOfBirth')}
              placeholder={t('dayMonthYear')}
              // @ts-ignore
              value={checkout.insurer.dateOfbirth || ''}
              onChange={(value: string) => {
                return [
                  dispatch(
                    setInsurerInfo({
                      fieldName: 'dateOfbirth',
                      fieldValue: value,
                    }),
                  ),
                  formik.setFieldValue('dateOfbirth', value),
                ]
              }}
              disabled={
                (auth?.user?.birthday && auth.user?.userType === 'User') || checkout.insurer.dateOfbirth ? true : false
              }
            />

            {checkout.insurer.dateOfbirth && diffage && diffage < 18 && (
              <WarningMessage variant="light-red" text={t('ageIsNotValid')} />
            )}

            {checkout.insurer.cityzenship === 'GE' && (
              <Input
                id="georgian-first-name"
                name="firstNameGe"
                label={`${t('nameInGeorgia')}`}
                type="text"
                placeholder=""
                value={checkout.insurer.translations?.ge?.firstName || ''}
                onChange={(value) => [
                  dispatch(
                    setInsurerTranslationInfo({
                      language: 'ge',
                      fieldName: 'firstName',
                      fieldValue: value,
                    }),
                  ),
                  formik.setFieldValue('firstNameGe', value),
                ]}
                width={'fullWidth'}
                error={formik.errors.firstNameGe && checkout.insurer.translations?.ge?.firstName ? true : false}
                errorText={formik.errors.firstNameGe}
                onBlur={formik.handleBlur}
                disabled={
                  (auth.user?.userType === 'User' && auth?.user?.firstNameGe) ||
                  checkout.insurer.translations?.ge?.firstName
                    ? true
                    : false
                }
              />
            )}

            {checkout.insurer.cityzenship === 'GE' && (
              <Input
                id="georgian-last-name"
                name="lastNameGe"
                label={`${t('surnameInGeorgia')}`}
                type="text"
                placeholder=""
                value={checkout.insurer.translations?.ge?.lastName || ''}
                onChange={(value) => [
                  dispatch(
                    setInsurerTranslationInfo({
                      language: 'ge',
                      fieldName: 'lastName',
                      fieldValue: value,
                    }),
                  ),
                  formik.setFieldValue('lastNameGe', value),
                ]}
                width={'fullWidth'}
                error={formik.errors.lastNameGe && checkout.insurer.translations?.ge?.lastName ? true : false}
                errorText={formik.errors.lastNameGe}
                onBlur={formik.handleBlur}
                disabled={
                  (auth.user?.userType === 'User' && auth?.user?.lastNameGe) ||
                  (checkout.insurer.sgsVerification === true && checkout.insurer.translations?.ge?.lastName)
                    ? true
                    : false
                }
              />
            )}

            <Input
              id="english-first-name"
              name="firstName"
              label={`${t('nameInEnglish')}`}
              type="text"
              placeholder=""
              value={checkout.insurer.translations?.en?.firstName || ''}
              onChange={(value) => [
                dispatch(
                  setInsurerTranslationInfo({
                    language: 'en',
                    fieldName: 'firstName',
                    fieldValue: value,
                  }),
                ),
                formik.setFieldValue('firstName', value),
              ]}
              width={'fullWidth'}
              error={formik.errors.firstName && checkout.insurer.translations?.en?.firstName ? true : false}
              errorText={formik.errors.firstName}
              onBlur={formik.handleBlur}
              disabled={auth.user?.userType === 'User' && auth?.user?.firstName ? true : false}
            />

            <Input
              id="english-last-name"
              name="lastName"
              label={`${t('surnameInEnglish')}`}
              type="text"
              placeholder=""
              value={checkout.insurer.translations?.en?.lastName || ''}
              onChange={(value) => [
                dispatch(
                  setInsurerTranslationInfo({
                    language: 'en',
                    fieldName: 'lastName',
                    fieldValue: value,
                  }),
                ),
                formik.setFieldValue('lastName', value),
              ]}
              width={'fullWidth'}
              error={formik.errors.lastName && checkout.insurer.translations?.en?.lastName ? true : false}
              errorText={formik.errors.lastName}
              onBlur={formik.handleBlur}
              disabled={auth.user?.userType === 'User' && auth?.user?.lastName ? true : false}
            />
            <>
              <ChooseGenderLabel>{t('chooseGender')}</ChooseGenderLabel>
              <GenderBlock
                style={{
                  userSelect:
                    (auth.user?.userType === 'User' && auth?.user?.gender) ||
                    (checkout.insurer.sgsVerification === true && checkout.insurer.gender)
                      ? 'none'
                      : 'unset',
                  //
                  pointerEvents:
                    (auth.user?.userType === 'User' && auth?.user?.gender) ||
                    (checkout.insurer.sgsVerification === true && checkout.insurer.gender)
                      ? 'none'
                      : 'unset',
                }}
              >
                <GenderCard
                  selected={checkout.insurer.gender === 'female'}
                  gender="female"
                  femaleText={`${t('female')}`}
                  onClick={() => [
                    dispatch(
                      setInsurerInfo({
                        fieldName: 'gender',
                        fieldValue: 'female',
                      }),
                    ),
                    formik.setFieldValue('gender', 'female'),
                  ]}
                  disabled={false}
                />

                <GenderCard
                  selected={checkout.insurer.gender === 'male'}
                  gender="male"
                  maleText={`${t('male')}`}
                  onClick={() => [
                    dispatch(
                      setInsurerInfo({
                        fieldName: 'gender',
                        fieldValue: 'male',
                      }),
                    ),
                    formik.setFieldValue('gender', 'male'),
                  ]}
                  disabled={false}
                />
              </GenderBlock>
            </>

            <>
              <hr />
              <BoxTitle>{t('yourID')}</BoxTitle>
              <Col>
                <UploadFile
                  label={t('side1')}
                  onChange={(value) => {
                    dispatch(
                      setInsurerInfo({
                        fieldName: 'IdImageFront',
                        fieldValue: value,
                      }),
                    )
                  }}
                  defaultFileName={
                    checkout?.insurer?.IdImageFront ? splittingImageTitle(checkout.insurer.IdImageFront) : ''
                  }
                />
              </Col>
              <Col>
                <UploadFile
                  label={t('side2')}
                  onChange={(value) => {
                    dispatch(
                      setInsurerInfo({
                        fieldName: 'IdImageBack',
                        fieldValue: value,
                      }),
                    )
                  }}
                  defaultFileName={
                    checkout?.insurer?.IdImageBack ? splittingImageTitle(checkout?.insurer?.IdImageBack) : ''
                  }
                />
              </Col>
            </>
          </Inputs>
        </WhiteBox>

        <Buttons>
          <NavigationButton text={`${t('back')}`} buttonType="prev" onClick={() => navigate('/health-for-other')} />
          <NavigationButton
            text={`${t('next')}`}
            buttonType="next"
            disabled={false}
            onClick={() => navigate('/health-check-information')}
          />
        </Buttons>
      </Content>

      <BackgroundIcon />
    </Wrapper>
  )
}

const BirthDayInputStyle = styled(BirthDayInput)`
  label {
    font-weight: normal;
    margin-bottom: 5px;
  }
`

const GenderBlock = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-template-rows: 1fr;
  grid-column-gap: 20px;
  grid-row-gap: 0px;
`
const ChooseGenderLabel = styled.label`
  font-size: 14px;
  color: ${Colors.primaryGrey};
`
const Inputs = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: 1fr;
  grid-column-gap: 0px;
  grid-row-gap: 20px;
  margin-top: 20px;
`
const WhiteBox = styled.div`
  background-color: ${Colors.white};
  margin-block: 32px;
  box-shadow: 0 4px 50px 0 rgba(0, 0, 0, 0.04);
  max-width: 464px;
  margin-inline: auto;
  border-radius: 8px;
  padding: 48px;

  ${Responsive.mobile} {
    padding: 20px;
  }
`
const BoxTitle = styled.h6`
  font-size: 18px;
  color: ${Colors.primaryBlack};
`

const Buttons = styled.div`
  display: flex;
  gap: 20px;
  width: min-content;
  margin-top: 48px;
  margin-inline: auto;
  button {
    width: 175px;
  }

  ${Responsive.mobile} {
    margin-top: 40px;
    gap: 16px;
    button {
      width: 140px;
    }
  }
`
const Content = styled.div`
  margin-top: 58px;
  z-index: 2;
  padding-inline: 20px;

  ${Responsive.mobile} {
    margin-top: 26px;
  }
`
const Wrapper = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  background-color: ${Colors.backgroundGrey};
  padding-bottom: 100px;

  ${Responsive.mobile} {
    padding-bottom: 48px;
  }
`

export default HealthInsurer
